import { type TransferEligibilityRealEstate } from "@/features/salesforceIntegration/types";

/**
 * FeedGridの1行が示すSalesforce連携の状態。
 *
 * 1行が複数の物件（`uniqueId`）を束ねるため、行の状態は物件単位の判定結果の集約になる。
 * 送信済みは物件単位で記録されるので、同じ行に「連携済み」と「未連携」が同居しうる。
 */
export type RowTransferState =
  | { kind: "UNKNOWN" }
  | { kind: "TRANSFERABLE" }
  | {
      kind: "PARTIALLY_TRANSFERRED";
      transferredCount: number;
      totalCount: number;
    }
  | { kind: "TRANSFERRED" }
  | { kind: "RUNNING" }
  | { kind: "BLOCKED"; reasonMessage: string };

/**
 * 行が持つ`uniqueId`群から、Salesforce列に出す状態を決める。
 *
 * 表示の根拠はすべてバックエンドの判定結果で、解析の完了などをここで再判定しない
 * （実装計画書7.4節）。唯一の例外が「連携中」で、実行中の対象物件は画面側しか知らない。
 *
 * @param inFlightUniqueIds 実行中の連携ジョブへ送った物件。画面で送信した場合のみ分かる
 */
export const resolveRowTransferState = (
  uniqueIds: readonly string[],
  realEstateByUniqueId: ReadonlyMap<string, TransferEligibilityRealEstate>,
  inFlightUniqueIds: ReadonlySet<string> = new Set()
): RowTransferState => {
  // 送った物件が分かっている間は、その行だけを実行中にする。
  // 判定APIは実行中をリクエスト単位で返すため、任せると送っていない行まで実行中になる
  if (uniqueIds.some((uniqueId) => inFlightUniqueIds.has(uniqueId))) {
    return { kind: "RUNNING" };
  }

  const realEstates = uniqueIds
    .map((uniqueId) => realEstateByUniqueId.get(uniqueId))
    .filter(
      (realEstate): realEstate is TransferEligibilityRealEstate =>
        realEstate !== undefined
    );

  if (realEstates.length === 0) {
    return { kind: "UNKNOWN" };
  }

  // 送った物件が分からない場合（実行中にリロードした等）は判定APIの実行中に従う。
  // 対象を絞れないため、この場合だけは全行が実行中になる
  if (
    inFlightUniqueIds.size === 0 &&
    realEstates.some(
      (realEstate) => realEstate.reasonCode === "TRANSFER_IN_PROGRESS"
    )
  ) {
    return { kind: "RUNNING" };
  }

  const transferableCount = realEstates.filter(
    (realEstate) => realEstate.isTransferable
  ).length;
  const transferredCount = realEstates.filter(
    (realEstate) => realEstate.reasonCode === "ALREADY_TRANSFERRED"
  ).length;

  if (transferableCount > 0) {
    return transferredCount > 0
      ? {
          kind: "PARTIALLY_TRANSFERRED",
          transferredCount,
          totalCount: realEstates.length,
        }
      : { kind: "TRANSFERABLE" };
  }

  if (transferredCount === realEstates.length) {
    return { kind: "TRANSFERRED" };
  }

  // 送信済み以外の理由を優先して見せる。全部が送信済みなら上の分岐で返っている
  const blocked = realEstates.find(
    (realEstate) =>
      realEstate.reasonCode !== "ALREADY_TRANSFERRED" &&
      realEstate.reasonMessage !== null
  );
  return { kind: "BLOCKED", reasonMessage: blocked?.reasonMessage ?? "" };
};
